// IANA time-zone identifier -> ISO 3166-1 alpha-2 country code.
//
// Why a table: the browser gives us a zone id
// (`Intl.DateTimeFormat().resolvedOptions().timeZone`), and there is no built-in way to
// turn that into a country. `Intl.Locale.prototype.timeZones` would do it, but it is not
// supported widely enough to rely on (it is absent in the browsers we tested).
//
// Why the zone id and not the UTC offset: the offset is ambiguous (Ukraine, Finland,
// Greece, Romania, Egypt and Latvia all sit at the same offset today), while the zone id
// names a city and so resolves to one country in almost every case.
//
// Legacy names: browsers differ on which spelling is canonical. Chrome still reports
// `Europe/Kiev` and `Asia/Calcutta` rather than the renamed `Europe/Kyiv` /
// `Asia/Kolkata`, so both spellings are listed.
//
// Coverage is the commercially relevant countries rather than all ~420 zones. An
// unmapped zone returns null and the caller falls back to its previous behaviour, so a
// missing entry is never worse than not having this table. To extend, add the zone id.
const TZ_TO_COUNTRY = {
  // ---- Europe ----
  "Europe/London": "GB",
  "Europe/Dublin": "IE",
  "Europe/Lisbon": "PT",
  "Atlantic/Azores": "PT",
  "Atlantic/Madeira": "PT",
  "Europe/Madrid": "ES",
  "Atlantic/Canary": "ES",
  "Europe/Paris": "FR",
  "Europe/Brussels": "BE",
  "Europe/Amsterdam": "NL",
  "Europe/Luxembourg": "LU",
  "Europe/Berlin": "DE",
  "Europe/Busingen": "DE",
  "Europe/Zurich": "CH",
  "Europe/Vaduz": "LI",
  "Europe/Vienna": "AT",
  "Europe/Rome": "IT",
  "Europe/Vatican": "VA",
  "Europe/San_Marino": "SM",
  "Europe/Malta": "MT",
  "Europe/Monaco": "MC",
  "Europe/Andorra": "AD",
  "Europe/Gibraltar": "GI",
  "Europe/Copenhagen": "DK",
  "Atlantic/Faroe": "FO",
  "Europe/Oslo": "NO",
  "Europe/Stockholm": "SE",
  "Europe/Helsinki": "FI",
  "Europe/Tallinn": "EE",
  "Europe/Riga": "LV",
  "Europe/Vilnius": "LT",
  "Europe/Warsaw": "PL",
  "Europe/Prague": "CZ",
  "Europe/Bratislava": "SK",
  "Europe/Budapest": "HU",
  "Europe/Ljubljana": "SI",
  "Europe/Zagreb": "HR",
  "Europe/Sarajevo": "BA",
  "Europe/Belgrade": "RS",
  "Europe/Podgorica": "ME",
  "Europe/Skopje": "MK",
  "Europe/Tirane": "AL",
  "Europe/Athens": "GR",
  "Europe/Sofia": "BG",
  "Europe/Bucharest": "RO",
  "Europe/Chisinau": "MD",
  "Europe/Kyiv": "UA",
  "Europe/Kiev": "UA",
  "Europe/Uzhgorod": "UA",
  "Europe/Zaporozhye": "UA",
  "Europe/Minsk": "BY",
  "Europe/Istanbul": "TR",
  "Asia/Istanbul": "TR",
  "Atlantic/Reykjavik": "IS",
  "Europe/Moscow": "RU",
  "Europe/Kaliningrad": "RU",
  "Europe/Samara": "RU",
  "Asia/Yekaterinburg": "RU",
  "Asia/Omsk": "RU",
  "Asia/Novosibirsk": "RU",
  "Asia/Krasnoyarsk": "RU",
  "Asia/Irkutsk": "RU",
  "Asia/Yakutsk": "RU",
  "Asia/Vladivostok": "RU",
  "Asia/Magadan": "RU",
  "Asia/Kamchatka": "RU",

  // ---- North America ----
  "America/New_York": "US",
  "America/Detroit": "US",
  "America/Indiana/Indianapolis": "US",
  "America/Kentucky/Louisville": "US",
  "America/Chicago": "US",
  "America/Denver": "US",
  "America/Phoenix": "US",
  "America/Los_Angeles": "US",
  "America/Anchorage": "US",
  "Pacific/Honolulu": "US",
  "America/Puerto_Rico": "PR",
  "America/Toronto": "CA",
  "America/Winnipeg": "CA",
  "America/Regina": "CA",
  "America/Edmonton": "CA",
  "America/Vancouver": "CA",
  "America/Halifax": "CA",
  "America/St_Johns": "CA",
  "America/Mexico_City": "MX",
  "America/Monterrey": "MX",
  "America/Cancun": "MX",
  "America/Tijuana": "MX",

  // ---- Central America and Caribbean ----
  "America/Guatemala": "GT",
  "America/El_Salvador": "SV",
  "America/Tegucigalpa": "HN",
  "America/Managua": "NI",
  "America/Costa_Rica": "CR",
  "America/Panama": "PA",
  "America/Havana": "CU",
  "America/Santo_Domingo": "DO",
  "America/Port-au-Prince": "HT",
  "America/Jamaica": "JM",
  "America/Nassau": "BS",
  "America/Barbados": "BB",
  "America/Port_of_Spain": "TT",

  // ---- South America ----
  "America/Bogota": "CO",
  "America/Lima": "PE",
  "America/Caracas": "VE",
  "America/La_Paz": "BO",
  "America/Guayaquil": "EC",
  "America/Santiago": "CL",
  "America/Argentina/Buenos_Aires": "AR",
  "America/Montevideo": "UY",
  "America/Asuncion": "PY",
  "America/Sao_Paulo": "BR",
  "America/Manaus": "BR",
  "America/Fortaleza": "BR",
  "America/Recife": "BR",
  "America/Bahia": "BR",
  "America/Paramaribo": "SR",
  "America/Guyana": "GY",

  // ---- Middle East ----
  "Asia/Jerusalem": "IL",
  "Asia/Tel_Aviv": "IL",
  "Asia/Beirut": "LB",
  "Asia/Damascus": "SY",
  "Asia/Amman": "JO",
  "Asia/Baghdad": "IQ",
  "Asia/Kuwait": "KW",
  "Asia/Riyadh": "SA",
  "Asia/Bahrain": "BH",
  "Asia/Qatar": "QA",
  "Asia/Dubai": "AE",
  "Asia/Muscat": "OM",
  "Asia/Aden": "YE",
  "Asia/Tehran": "IR",

  // ---- Caucasus and Central Asia ----
  "Asia/Baku": "AZ",
  "Asia/Yerevan": "AM",
  "Asia/Tbilisi": "GE",
  "Asia/Almaty": "KZ",
  "Asia/Aqtobe": "KZ",
  "Asia/Tashkent": "UZ",
  "Asia/Ashgabat": "TM",
  "Asia/Dushanbe": "TJ",
  "Asia/Bishkek": "KG",
  "Asia/Ulaanbaatar": "MN",

  // ---- South and South-East Asia ----
  "Asia/Kabul": "AF",
  "Asia/Karachi": "PK",
  "Asia/Kolkata": "IN",
  "Asia/Calcutta": "IN",
  "Asia/Colombo": "LK",
  "Asia/Kathmandu": "NP",
  "Asia/Dhaka": "BD",
  "Asia/Yangon": "MM",
  "Asia/Bangkok": "TH",
  "Asia/Ho_Chi_Minh": "VN",
  "Asia/Saigon": "VN",
  "Asia/Phnom_Penh": "KH",
  "Asia/Vientiane": "LA",
  "Asia/Kuala_Lumpur": "MY",
  "Asia/Singapore": "SG",
  "Asia/Jakarta": "ID",
  "Asia/Makassar": "ID",
  "Asia/Jayapura": "ID",
  "Asia/Manila": "PH",

  // ---- East Asia ----
  "Asia/Hong_Kong": "HK",
  "Asia/Macau": "MO",
  "Asia/Taipei": "TW",
  "Asia/Shanghai": "CN",
  "Asia/Urumqi": "CN",
  "Asia/Seoul": "KR",
  "Asia/Pyongyang": "KP",
  "Asia/Tokyo": "JP",

  // ---- Oceania ----
  "Australia/Sydney": "AU",
  "Australia/Melbourne": "AU",
  "Australia/Brisbane": "AU",
  "Australia/Adelaide": "AU",
  "Australia/Perth": "AU",
  "Australia/Darwin": "AU",
  "Australia/Hobart": "AU",
  "Pacific/Auckland": "NZ",
  "Pacific/Fiji": "FJ",
  "Pacific/Guam": "GU",
  "Pacific/Port_Moresby": "PG",

  // ---- Africa ----
  "Africa/Cairo": "EG",
  "Africa/Casablanca": "MA",
  "Africa/Algiers": "DZ",
  "Africa/Tunis": "TN",
  "Africa/Tripoli": "LY",
  "Africa/Khartoum": "SD",
  "Africa/Lagos": "NG",
  "Africa/Accra": "GH",
  "Africa/Abidjan": "CI",
  "Africa/Dakar": "SN",
  "Africa/Bamako": "ML",
  "Africa/Nairobi": "KE",
  "Africa/Addis_Ababa": "ET",
  "Africa/Kampala": "UG",
  "Africa/Dar_es_Salaam": "TZ",
  "Africa/Kigali": "RW",
  "Africa/Kinshasa": "CD",
  "Africa/Luanda": "AO",
  "Africa/Harare": "ZW",
  "Africa/Lusaka": "ZM",
  "Africa/Maputo": "MZ",
  "Africa/Gaborone": "BW",
  "Africa/Windhoek": "NA",
  "Africa/Johannesburg": "ZA",
  "Indian/Mauritius": "MU"
}

// Case-insensitive lookup, since the exact casing a browser reports is not guaranteed.
const LOOKUP = Object.entries(TZ_TO_COUNTRY).reduce((acc, [tz, cc]) => {
  acc[tz.toLowerCase()] = cc
  return acc
}, {})

// The browser's own time zone, or '' when unavailable.
export const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ""
  } catch {
    return ""
  }
}

// ISO country code for a zone id, or null when the zone is not in the table.
export const countryFromTimeZone = (tz) => {
  if (!tz || typeof tz !== "string") return null
  return LOOKUP[tz.trim().toLowerCase()] || null
}

// Convenience: the country for THIS browser, or null.
export const browserCountryFromTimeZone = () => countryFromTimeZone(browserTimeZone())
